import {Decimal} from 'decimal.js';

import type {OfferedTrimRequestBody, TrimResponseBody} from '@dms/api/new-vehicle';

import type {PricedItemChange} from '../types/PricedItemChange';
import {createdItem} from './createdItem';
import {isInPricelist} from './isInPricelist';
import {removedItem} from './removedItem';

type Trim = Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>;
type Row = Pick<OfferedTrimRequestBody, 'trimId' | 'priceNet'>;
/** The item with its rows: `isMember` when the item itself is in the price list (a colour not archived, an active package). */
type OfferedItem = {name: string; rows: Row[]; isMember: boolean};

/** The rows of an item that are in the dealer price list: those on an online trim of a member item (D102). */
export const memberRows = <R extends Row>(rows: R[], trims: Trim[], isMember: boolean) =>
  isMember
    ? rows.filter(({trimId}) =>
        isInPricelist.trim(trims.find(({id}) => id === trimId)?.publicationStatus)
      )
    : [];

/**
 * Whether "Same price on all trims" at `priceNet` changes a member row the form still offers: on a
 * published year only a price adjustment changes such a price (D102), and one that changes nothing
 * is refused, so the form fills its rows itself otherwise. A row stored at 0 (included) is no
 * target: the adjustment leaves it, as the form's fill does.
 */
export const changesKeptPrice = <R extends Row>(
  rows: R[],
  trims: Trim[],
  isMember: boolean,
  offered: {trimId: string; isOffered: boolean}[],
  priceNet: string
) =>
  memberRows(rows, trims, isMember).some(
    (kept) =>
      offered.some((row) => row.trimId === kept.trimId && row.isOffered) &&
      !new Decimal(kept.priceNet).isZero() &&
      !new Decimal(kept.priceNet).equals(priceNet)
  );

/**
 * The colour or package rows a write puts into the dealer price list (— → price) or takes out of it
 * (stored price → —), named "{trim} · {item}" as the server names them. A row in the price list
 * before and after is kept: its price changes only by price adjustment, so it is no row here.
 */
export const offeredTrimChanges = (options: {
  resourceType: 'colourTrim' | 'packageTrim';
  trims: Trim[];
  stored: OfferedItem;
  next: OfferedItem;
}): PricedItemChange[] => {
  const trimName = (trimId: string) => options.trims.find(({id}) => id === trimId)?.name ?? trimId;
  const stored = memberRows(options.stored.rows, options.trims, options.stored.isMember);
  const next = memberRows(options.next.rows, options.trims, options.next.isMember);
  const isIn = (rows: Row[], trimId: string) => rows.some((row) => row.trimId === trimId);
  const key = (trimId: string) => `${options.resourceType}:${trimId}`;
  return [
    ...stored
      .filter(({trimId}) => !isIn(next, trimId))
      .map(({trimId, priceNet}) =>
        removedItem(
          options.resourceType,
          key(trimId),
          `${trimName(trimId)} · ${options.stored.name}`,
          new Decimal(priceNet).toFixed(2)
        )
      ),
    ...next
      .filter(({trimId}) => !isIn(stored, trimId))
      .map(({trimId, priceNet}) =>
        createdItem(
          options.resourceType,
          key(trimId),
          `${trimName(trimId)} · ${options.next.name}`,
          new Decimal(priceNet).toFixed(2)
        )
      ),
  ];
};
